import moment from 'moment';
import HelperMixin from '../../Shared/HelperMixin.vue';

const TABS = [
    { key: 'stock', label: 'المخزون مقابل الصرف' },
    { key: 'movement', label: 'حركة الأصناف بالاستهلاك' },
    { key: 'batches', label: 'التشغيلات والصلاحية' },
    { key: 'gap', label: 'الفائض والعجز' },
    { key: 'accuracy', label: 'دقة الاحتياج' },
    { key: 'suggest', label: 'الاحتياج المقترح' },
    { key: 'specialized', label: 'الأدوية التخصصية' },
];

export default {
    name: 'PharmacyReports',
    mixins: [HelperMixin],

    async created() {
        await this.CheckLoginStatus();
        // نفس حارس الخادم بالضبط: إدارة الصيدلة ومدير النظام.
        if (!this.loginDetails || !this.hasAdministratorOperationalAccessUi()) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'التقارير الوطنية متاحة لإدارة الصيدلة ومدير النظام.');
            this.href('/Home');
            return;
        }
        this.loadLookups();
        this.runReport();
    },

    filters: {
        momentDate(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY');
        },
    },

    data() {
        const to = moment().format('YYYY-MM-DD');
        const from = moment().subtract(30, 'days').format('YYYY-MM-DD');
        return {
            tabs: TABS,
            activeTab: 'stock',

            filters: {
                from,
                to,
                branchId: null,
                productsId: null,
            },

            branches: [],
            products: [],
            productSearch: '',
            loadingLookups: false,

            loading: false,
            rows: [],
            totals: null,
            period: null,

            // تبويب التشغيلات
            batchFilter: '',
            batchSettings: null,

            // اعتماد الاحتياج المقترح
            selectedSuggestions: [],
            coveragePercent: 100,
            applying: false,

            // الأدوية التخصصية وحدها — لتنظيم متابعتها
            specializedOnly: false,
            specializedLabel: null,
            specializedSearch: '',

            // تجميع تقرير المخزون: both | product | branch
            groupBy: 'both',
            // ترتيب الاستهلاك: most | least
            consumptionSort: 'most',
        };
    },

    computed: {
        /** الصنف المختار — لتبويب الفائض والعجز الذي يعمل على الصنف النمطي. */
        selectedProduct() {
            const id = Number(this.filters.productsId || 0);
            if (!id) return null;
            return this.products.find((p) => Number(p.id) === id) || null;
        },
        periodLabel() {
            if (!this.period) return '';
            return `${moment(this.period.from).format('DD/MM/YYYY')} — ${moment(this.period.to).format('DD/MM/YYYY')}`;
        },
        /** الفترة لا معنى لها في تقريري الرصيد اللحظي — تُخفى بدل أن تُعرض بلا أثر. */
        periodApplies() {
            return this.activeTab === 'stock' || this.activeTab === 'movement'
                || this.activeTab === 'accuracy' || this.activeTab === 'suggest';
        },

        /** لوحة التخصصية تعرض الوضع اللحظي — فلاتر الجهة والصنف لا تنطبق عليها. */
        usesGlobalFilters() {
            return this.activeTab !== 'specialized';
        },
    },

    methods: {
        loadLookups() {
            this.loadingLookups = true;
            this.$http.GetPharmacyReportLookups(this.productSearch, this.specializedOnly)
                .then((res) => {
                    this.loadingLookups = false;
                    const d = (res && res.data) || {};
                    this.branches = d.branches || [];
                    this.products = d.products || [];
                    this.specializedLabel = d.specializedLabel || null;
                })
                .catch(() => {
                    this.loadingLookups = false;
                });
        },

        onProductSearch(term) {
            this.productSearch = term || '';
            this.loadLookups();
        },

        selectTab(key) {
            if (this.activeTab === key) return;
            this.activeTab = key;
            this.rows = [];
            this.totals = null;
            this.selectedSuggestions = [];
            this.runReport();
        },

        resetFilters() {
            this.filters = {
                from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
                to: moment().format('YYYY-MM-DD'),
                branchId: null,
                productsId: null,
            };
            this.batchFilter = '';
            this.runReport();
        },

        runReport() {
            this.loading = true;
            this.rows = [];

            const done = (res) => {
                this.loading = false;
                const d = (res && res.data) || {};
                this.rows = d.info || [];
                this.totals = d.totals || null;
                this.period = d.period || null;
                if (d.settings) this.batchSettings = d.settings;
            };
            const fail = (err) => {
                this.loading = false;
                this.rows = [];
                this.totals = null;
                const msg = err.response && err.response.data ? err.response.data : 'تعذّر تحميل التقرير.';
                this.$helper.ShowMessage('error', 'خطأ', msg);
            };

            if (this.activeTab === 'stock') {
                this.$http.GetPharmacyStockVsDispense({ ...this.filters, groupBy: this.groupBy })
                    .then(done).catch(fail);
            } else if (this.activeTab === 'movement') {
                this.$http.GetPharmacyItemMovement({ ...this.filters, sort: this.consumptionSort })
                    .then(done).catch(fail);
            } else if (this.activeTab === 'batches') {
                this.$http.GetPharmacyBatchesExpiry({
                    branchId: this.filters.branchId,
                    productsId: this.filters.productsId,
                    filter: this.batchFilter,
                }).then(done).catch(fail);
            } else if (this.activeTab === 'gap') {
                this.$http.GetPharmacySurplusDeficit({
                    branchId: this.filters.branchId,
                    formularyItemId: this.selectedProduct ? this.selectedProduct.formularyItemId : null,
                }).then(done).catch(fail);
            } else if (this.activeTab === 'accuracy') {
                this.$http.GetPharmacyNeedAccuracy({
                    ...this.filters,
                    formularyItemId: this.selectedProduct ? this.selectedProduct.formularyItemId : null,
                }).then(done).catch(fail);
            } else if (this.activeTab === 'suggest') {
                this.$http.GetPharmacySuggestedNeeds({
                    ...this.filters,
                    formularyItemId: this.selectedProduct ? this.selectedProduct.formularyItemId : null,
                }).then(done).catch(fail);
            } else if (this.activeTab === 'specialized') {
                this.$http.GetSpecializedOverview(this.specializedSearch)
                    .then((res) => {
                        done(res);
                        const d = (res && res.data) || {};
                        this.specializedLabel = d.specializedLabel || null;
                    })
                    .catch(fail);
            }
        },

        /** تبديل نطاق الأصناف يُفرغ الاختيار: صنف عام مختار لا معنى له في نطاق تخصصي. */
        toggleSpecialized() {
            this.specializedOnly = !this.specializedOnly;
            this.filters.productsId = null;
            this.loadLookups();
            this.runReport();
        },

        /* ── اعتماد الاحتياج المقترح ───────────────────────────── */

        suggestionKey(r) {
            return `${r.branchesId}:${r.formularyItemId}`;
        },

        isSuggestionSelected(r) {
            return this.selectedSuggestions.indexOf(this.suggestionKey(r)) >= 0;
        },

        toggleSuggestion(r) {
            const k = this.suggestionKey(r);
            const i = this.selectedSuggestions.indexOf(k);
            if (i >= 0) this.selectedSuggestions.splice(i, 1);
            else this.selectedSuggestions.push(k);
        },

        toggleAllSuggestions() {
            if (this.selectedSuggestions.length === this.rows.length) {
                this.selectedSuggestions = [];
            } else {
                this.selectedSuggestions = this.rows.map((r) => this.suggestionKey(r));
            }
        },

        /** الاحتياج الكلي = الشهري × 12 — يُعرض ولا يُخزَّن، فلا تنشأ قيمتان متناقضتان. */
        annualOf(monthly) {
            return Math.round(Number(monthly || 0) * 12);
        },

        /** القيمة التي ستُكتب فعلاً بعد نسبة الاعتماد — تُعرض قبل الحفظ لا بعده. */
        effectiveNeed(r) {
            const pct = Number(this.coveragePercent || 100);
            return Math.ceil(Number(r.suggestedMonthlyNeed || 0) * pct / 100);
        },

        applySuggestions() {
            const keys = new Set(this.selectedSuggestions);
            const lines = (this.rows || [])
                .filter((r) => keys.has(this.suggestionKey(r)))
                .map((r) => ({
                    branchesId: r.branchesId,
                    formularyItemId: r.formularyItemId,
                    monthlyNeed: Number(r.suggestedMonthlyNeed || 0),
                }));

            if (!lines.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'اختر سطراً واحداً على الأقل لاعتماده.');
                return;
            }

            this.$confirm(
                `سيُكتب الاحتياج لـ${lines.length} سطراً بنسبة ${this.coveragePercent}% من المعدل المقيس، ويحلّ محل المسجَّل حالياً. متابعة؟`,
                'اعتماد الاحتياج المقترح',
                { confirmButtonText: 'اعتماد', cancelButtonText: 'تراجع', type: 'warning' }
            ).then(() => {
                this.applying = true;
                this.$http.ApplySuggestedNeeds({
                    coveragePercent: Number(this.coveragePercent || 100),
                    lines,
                })
                    .then((res) => {
                        this.applying = false;
                        this.selectedSuggestions = [];
                        this.$helper.ShowMessage('success', 'تم', (res.data && res.data.message) || 'تم الاعتماد.');
                        this.runReport();
                    })
                    .catch((err) => {
                        this.applying = false;
                        const msg = err.response && err.response.data ? err.response.data : 'تعذّر الاعتماد.';
                        this.$helper.ShowMessage('error', 'خطأ', msg);
                    });
            }).catch(() => { });
        },

        /** حالة الصنف التخصصي بجملة واحدة — أوضح من قراءة ستة أعمدة. */
        specializedStateLabel(r) {
            if (r.isCritical) return 'نفد ولا شيء في الطريق';
            if (r.isStuckAtSupply) return 'الرصيد محتجز في الإمداد';
            if (r.outstandingRequests > 0 && r.inPipeline > 0) return 'مطلوب وفي الطريق';
            if (r.outstandingRequests > 0) return 'مطلوب';
            if (r.inPipeline > 0) return 'في الطريق';
            return 'مستقر';
        },

        specializedStateClass(r) {
            if (r.isCritical) return 'bg-label-danger';
            if (r.isStuckAtSupply) return 'bg-label-warning';
            if (r.outstandingRequests > 0) return 'bg-label-info';
            return 'bg-label-secondary';
        },

        setGroupBy(value) {
            this.groupBy = value;
            this.runReport();
        },

        setConsumptionSort(value) {
            this.consumptionSort = value;
            this.runReport();
        },

        /** أقل من 50% = تقدير مبالغ · أكثر من 100% = تقدير أقل من الحاجة. */
        accuracyClass(row) {
            if (!row.hasDispensingData) return 'text-muted';
            if (row.accuracyPercent == null) return 'text-muted';
            if (row.accuracyPercent < 50) return 'text-warning fw-semibold';
            if (row.accuracyPercent > 100) return 'text-danger fw-semibold';
            return 'text-success fw-semibold';
        },

        setBatchFilter(value) {
            this.batchFilter = this.batchFilter === value ? '' : value;
            this.runReport();
        },

        /** تغطية أقل من شهر = خطر نفاد · أكثر من ستة أشهر = تكديس. */
        coverClass(row) {
            if (row.coverMonths == null) return 'text-muted';
            if (row.coverMonths < 1) return 'text-danger fw-semibold';
            if (row.coverMonths > 6) return 'text-warning fw-semibold';
            return 'text-success';
        },

        coverText(row) {
            if (row.coverMonths == null) return 'بلا صرف في الفترة';
            return `${row.coverMonths} شهر`;
        },

        expiryClass(row) {
            if (row.isExpired) return 'text-danger fw-semibold';
            if (row.isNearExpiry) return 'text-warning fw-semibold';
            return '';
        },

        gapClass(row) {
            if (row.isOutOfStock) return 'text-danger fw-bold';
            if (row.isDeficit) return 'text-danger';
            if (row.isSurplus) return 'text-warning';
            return 'text-success';
        },

        gapLabel(row) {
            if (row.isOutOfStock) return 'نفد';
            if (row.isDeficit) return 'عجز';
            if (row.isSurplus) return 'فائض';
            return 'متوازن';
        },

        /** تصدير ما هو معروض — بلا استدعاء ثانٍ فيختلف المصدَّر عن المعروض. */
        exportCsv() {
            if (!this.rows.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'لا توجد بيانات للتصدير.');
                return;
            }

            const keys = Object.keys(this.rows[0]);
            const escape = (v) => {
                if (v === null || v === undefined) return '';
                const s = String(v).replace(/"/g, '""');
                return /[",\n]/.test(s) ? `"${s}"` : s;
            };
            const lines = [keys.join(',')];
            this.rows.forEach((r) => lines.push(keys.map((k) => escape(r[k])).join(',')));

            // BOM حتى تفتح إكسل العربية بترميز صحيح.
            const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `pharmacy-report-${this.activeTab}-${moment().format('YYYYMMDD-HHmm')}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        },
    },
};
